import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Loader2, XCircle, CheckCircle2, Users } from 'lucide-react';
import { apiService } from '../../services/api';
import { authService } from '../../services/auth';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface FanpagePage {
  page_id: string;
  name: string;
  category: string;
  picture_url: string;
  fan_count: number;
  already_connected: boolean;
}

type Phase =
  | 'validating'
  | 'exchanging'
  | 'picking'
  | 'connecting'
  | 'error';

// ─────────────────────────────────────────────────────────────────────────────
// Page Picker row
// ─────────────────────────────────────────────────────────────────────────────
const PageRow: React.FC<{
  page: FanpagePage;
  selected: boolean;
  onSelect: () => void;
}> = ({ page, selected, onSelect }) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex items-center gap-4 w-full px-4 py-3.5 rounded-2xl border-2 text-left transition-all ${
        selected
          ? 'border-[#1877F2] bg-blue-50 shadow-md shadow-blue-100'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm cursor-pointer'
      }`}
    >
      <img
        src={
          page.picture_url ||
          `https://api.dicebear.com/7.x/shapes/svg?seed=${page.page_id}&backgroundColor=1877F2`
        }
        alt={page.name}
        className="w-12 h-12 rounded-xl object-cover bg-gray-100 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-bold text-gray-900 truncate">{page.name}</span>
          {page.already_connected && (
            <span className="text-[10px] font-bold bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full flex-shrink-0">
              Đã kết nối
            </span>
          )}
        </div>
        <div className="text-[12px] text-gray-400 flex items-center gap-1.5 mt-0.5">
          <span>{page.category}</span>
          {page.fan_count > 0 && (
            <>
              <span className="opacity-40">·</span>
              <Users size={11} className="flex-shrink-0" />
              <span>
                {page.fan_count >= 1000
                  ? `${(page.fan_count / 1000).toFixed(1)}k`
                  : page.fan_count}{' '}
                followers
              </span>
            </>
          )}
        </div>
      </div>
      <div
        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
          selected ? 'border-[#1877F2] bg-[#1877F2]' : 'border-gray-300'
        }`}
      >
        {selected && <Check size={13} className="text-white" />}
      </div>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared card wrapper
// ─────────────────────────────────────────────────────────────────────────────
const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-[#e8f2fd] via-white to-[#f0f4ff] flex items-center justify-center p-4">
    <div className="bg-white rounded-3xl shadow-2xl shadow-blue-100 w-full max-w-md overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-[#1877F2] to-[#42a5f5]" />
      <div className="px-8 py-8">{children}</div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const FanpageOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [phase, setPhase] = useState<Phase>('validating');
  const [errorMsg, setErrorMsg] = useState('');
  const [pages, setPages] = useState<FanpagePage[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [selectedPageId, setSelectedPageId] = useState('');
  const [authPath, setAuthPath] = useState<'nonce' | 'jwt' | ''>('');

  const hasUnconnectedPage = pages.some((p) => !p.already_connected);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code) {
      setErrorMsg('Không tìm thấy authorization code trong URL.');
      setPhase('error');
      return;
    }

    const savedState = sessionStorage.getItem('fanpage_oauth_state');
    if (state && savedState && state !== savedState) {
      setErrorMsg('State mismatch — có thể bị tấn công CSRF. Vui lòng thử lại.');
      setPhase('error');
      return;
    }

    sessionStorage.removeItem('fanpage_oauth_state');

    // Try to restore JWT (best-effort)
    if (authService.loadCachedIframeToken?.()) {
      const token = authService.getSessionToken();
      if (token) (apiService as any).setAuthToken?.(token);
    }

    exchangeCode(code, state || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchangeCode = async (code: string, nonce: string) => {
    setPhase('exchanging');
    const businessId = sessionStorage.getItem('fanpage_oauth_business_id') || authService.getBusinessId() || '';

    const applyPages = (incoming: FanpagePage[]) => {
      setPages(incoming);
      const preferred = incoming.find((p) => !p.already_connected) || incoming[0];
      setSelectedPageId(preferred?.page_id || '');
    };

    // ① Nonce path first (works even when JWT is unavailable in external browser)
    if (nonce) {
      try {
        const result = await apiService.post<any>('/api/v1/auth/fanpage/auth-with-nonce', { code, nonce });
        setSessionId(result.session_id);
        applyPages(result.pages ?? []);
        setAuthPath('nonce');
        setPhase('picking');
        return;
      } catch (nonceErr: any) {
        const errCode = nonceErr?.response?.data?.code;
        if (errCode !== 'NONCE_EXPIRED' && nonceErr?.response?.status !== 401) {
          setErrorMsg(
            nonceErr?.response?.data?.error ||
            nonceErr?.message ||
            'Không thể xác thực với Facebook.'
          );
          setPhase('error');
          return;
        }
        // Nonce expired — fall through to JWT path
      }
    }

    // ② JWT path
    try {
      const result = await apiService.post<any>('/api/v1/auth/fanpage', { code, business_id: businessId });
      setSessionId(result.session_id);
      applyPages(result.pages ?? []);
      setAuthPath('jwt');
      setPhase('picking');
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Không thể xác thực với Facebook. Vui lòng thử lại.';
      setErrorMsg(msg);
      setPhase('error');
    }
  };

  const handleConnect = async () => {
    if (!selectedPageId) return;
    setPhase('connecting');

    const selectedPage = pages.find((p) => p.page_id === selectedPageId);
    const reconnect = Boolean(selectedPage?.already_connected);

    const connectEndpoint =
      authPath === 'nonce'
        ? '/api/v1/auth/fanpage/connect-with-session'
        : '/api/v1/auth/fanpage/connect';

    try {
      await apiService.post<any>(connectEndpoint, {
        session_id: sessionId,
        page_id: selectedPageId,
        reconnect,
      });
      goBack(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Kết nối thất bại. Vui lòng thử lại.';
      setErrorMsg(msg);
      setPhase('picking');
    }
  };

  const BIZTADA_SHELL = 'https://biztada.com';

  const notifyOpenerAndClose = (connected: boolean) => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: 'FANPAGE_OAUTH_DONE', app: 'marketing', connected },
          window.location.origin
        );
        window.opener.focus();
        window.close();
        return;
      }
    } catch {
      // Ignore and fall back to redirect below.
    }
  };

  const goBack = (connected = false) => {
    notifyOpenerAndClose(connected);
    const url = connected ? `${BIZTADA_SHELL}?fanpage_connected=1` : BIZTADA_SHELL;
    window.location.href = url;
  };

  // ── LOADING ──────────────────────────────────────────────────────────────
  if (phase === 'validating' || phase === 'exchanging') {
    return (
      <Card>
        <div className="flex flex-col items-center text-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-[#1877F2] flex items-center justify-center shadow-lg shadow-blue-300">
            <Loader2 size={30} className="text-white animate-spin" />
          </div>
          <div>
            <p className="text-[16px] font-bold text-gray-900">Đang xử lý đăng nhập...</p>
            <p className="text-[13px] text-gray-400 mt-1">
              {phase === 'validating'
                ? 'Đang kiểm tra thông tin xác thực'
                : 'Đang lấy danh sách Fanpage của bạn'}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // ── CONNECTING ───────────────────────────────────────────────────────────
  if (phase === 'connecting') {
    return (
      <Card>
        <div className="flex flex-col items-center text-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-[#1877F2] flex items-center justify-center shadow-lg shadow-blue-300">
            <Loader2 size={30} className="text-white animate-spin" />
          </div>
          <div>
            <p className="text-[16px] font-bold text-gray-900">Đang kết nối Fanpage...</p>
            <p className="text-[13px] text-gray-400 mt-1">Đăng ký webhook và lưu cấu hình</p>
          </div>
        </div>
      </Card>
    );
  }

  // ── ERROR ────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <Card>
        <div className="flex flex-col items-center text-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center">
            <XCircle size={32} className="text-red-500" />
          </div>
          <div>
            <p className="text-[17px] font-bold text-gray-900">Xác thực thất bại</p>
            <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">{errorMsg}</p>
          </div>
          <button
            type="button"
            onClick={() => goBack()}
            className="w-full py-3 rounded-xl bg-gray-800 text-white font-bold text-[14px] hover:bg-gray-700 transition-all"
          >
            Quay về Biztada
          </button>
        </div>
      </Card>
    );
  }

  // ── PAGE PICKER ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f2fd] via-white to-[#f0f4ff] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl shadow-blue-100 w-full max-w-md overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-[#1877F2] to-[#42a5f5]" />
        <div className="px-8 pt-7 pb-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#1877F2] flex items-center justify-center flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </div>
            <div>
              <h1 className="text-[16px] font-bold text-gray-900 leading-tight">
                Chọn Facebook Fanpage
              </h1>
              <p className="text-[12px] text-gray-400 mt-0.5">
                Đăng nhập thành công · Chọn Fanpage muốn kết nối marketing
              </p>
            </div>
          </div>

          {/* Success banner */}
          <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-xl mb-5">
            <CheckCircle2 size={14} className="text-green-600 flex-shrink-0" />
            <p className="text-[12px] text-green-700 font-medium">
              Tìm thấy <strong>{pages.length}</strong> Fanpage bạn quản lý
            </p>
          </div>

          {pages.length > 0 && !hasUnconnectedPage && (
            <div className="flex items-start gap-2.5 px-3 py-3 bg-amber-50 border border-amber-200 rounded-xl mb-4">
              <AlertCircle size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-amber-700">
                Facebook hiện chỉ cấp quyền cho Fanpage đã kết nối. Hãy mở lại màn hình cấp quyền và bấm
                {' '}<strong>Chỉnh sửa phần cài đặt</strong>{' '}để chọn thêm Fanpage khác.

                        {pages.length === 1 && (
                          <div className="flex items-start gap-2.5 px-3 py-3 bg-red-50 border border-red-200 rounded-xl mb-4">
                            <AlertCircle size={15} className="text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="text-[12px] text-red-700">
                              <p className="font-medium mb-2">⚠️ Chỉ tìm thấy 1 Fanpage</p>
                              <ol className="list-decimal list-inside space-y-1 text-[11px]">
                                <li>Vui lòng <strong>Hủy</strong> ở dưới</li>
                                <li>Vào <strong>Facebook Settings → Apps and Websites → Tadagram</strong></li>
                                <li>Click <strong>Remove</strong> để xóa app hoàn toàn</li>
                                <li>Quay lại và click <strong>"+ Kết nối Fanpage"</strong> để kết nối lại</li>
                                <li>Lần này hãy chọn <strong>tất cả Fanpage</strong> bạn muốn quản lý</li>
                              </ol>
                            </div>
                          </div>
                        )}
              </p>
            </div>
          )}

          {/* Error from connect attempt */}
          {errorMsg && (
            <div className="flex items-start gap-2.5 px-3 py-3 bg-red-50 border border-red-200 rounded-xl mb-4">
              <AlertCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-red-700">{errorMsg}</p>
            </div>
          )}

          {/* Page list */}
          <div className="flex flex-col gap-2.5 max-h-[340px] overflow-y-auto pr-0.5 mb-5">
            {pages.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <p className="text-sm font-medium">Không tìm thấy Fanpage nào</p>
                <p className="text-xs mt-1">
                  Tài khoản Facebook của bạn chưa quản lý Fanpage nào.
                </p>
              </div>
            ) : (
              pages.map((page) => (
                <PageRow
                  key={page.page_id}
                  page={page}
                  selected={selectedPageId === page.page_id}
                  onSelect={() => setSelectedPageId(page.page_id)}
                />
              ))
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => goBack()}
              className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-[13px] hover:bg-gray-50 transition-all"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={handleConnect}
              disabled={!selectedPageId}
              className="flex-2 flex-grow-[2] flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl bg-[#1877F2] text-white font-bold text-[14px] hover:bg-[#166fe5] transition-all shadow-md shadow-blue-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <Check size={16} />
              {pages.find((p) => p.page_id === selectedPageId)?.already_connected
                ? 'Kết nối lại Fanpage này'
                : 'Kết nối Fanpage này'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FanpageOAuthCallback;
